import React, { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { WorkerAssignment } from '../../../services/worker'
import { openDirections } from '../../../services/mapsDirections'
import { requestEphemeralMapPosition, workerMapTiles, type EphemeralMapPosition } from '../../../services/workerJobMap'
import { useI18n } from '../../../i18n/I18nContext'

export const WorkerAssignmentMap: React.FC<{ assignment: WorkerAssignment; compact?: boolean }> = ({ assignment, compact = false }) => {
  const { t } = useI18n()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const layerRef = useRef<L.LayerGroup | null>(null)
  const [position, setPosition] = useState<EphemeralMapPosition | null>(null)
  const [locationState, setLocationState] = useState<'idle' | 'loading' | 'denied' | 'unavailable'>('idle')
  const mapLocation = assignment.mapLocation

  useEffect(() => {
    if (!containerRef.current || mapRef.current || !mapLocation?.available || mapLocation.latitude == null || mapLocation.longitude == null) return
    const map = L.map(containerRef.current, { zoomControl: true }).setView([mapLocation.latitude, mapLocation.longitude], 15)
    L.tileLayer(workerMapTiles.url, { attribution: workerMapTiles.attribution, maxZoom: workerMapTiles.maxZoom }).addTo(map)
    mapRef.current = map
    layerRef.current = L.layerGroup().addTo(map)
    return () => { map.remove(); mapRef.current = null; layerRef.current = null }
  }, [mapLocation?.available, mapLocation?.latitude, mapLocation?.longitude])

  useEffect(() => {
    const map = mapRef.current; const layer = layerRef.current
    if (!map || !layer || !mapLocation?.available || mapLocation.latitude == null || mapLocation.longitude == null) return
    layer.clearLayers()
    const jobPoint: L.LatLngExpression = [mapLocation.latitude, mapLocation.longitude]
    const bounds: L.LatLngExpression[] = [jobPoint]
    L.circleMarker(jobPoint, { radius: 9, color: '#2563eb', fillColor: '#2563eb', fillOpacity: .85, weight: 3 }).bindTooltip(mapLocation.displayLabel || t('Worksite')).addTo(layer)
    const geofence = mapLocation.geofence
    if (geofence?.type === 'circle' && geofence.centerLatitude != null && geofence.centerLongitude != null && geofence.radiusMeters != null) {
      L.circle([geofence.centerLatitude, geofence.centerLongitude], { radius: geofence.radiusMeters, color: '#2563eb', fillOpacity: .08, weight: 2 }).addTo(layer)
    } else if (geofence?.type === 'polygon' && geofence.polygon?.length) {
      L.polygon(geofence.polygon).addTo(layer)
    }
    if (position) {
      const workerPoint: L.LatLngExpression = [position.latitude, position.longitude]
      bounds.push(workerPoint)
      L.circleMarker(workerPoint, { radius: 8, color: '#047857', fillColor: '#10b981', fillOpacity: .9, weight: 3 }).bindTooltip(t('My location')).addTo(layer)
    }
    if (bounds.length > 1) map.fitBounds(L.latLngBounds(bounds), { padding: [28, 28], maxZoom: 15 })
    else map.setView(jobPoint, 15)
    map.invalidateSize()
  }, [mapLocation, position, t])

  if (!mapLocation?.available || mapLocation.latitude == null || mapLocation.longitude == null) return null
  const useLocation = async () => {
    setLocationState('loading')
    try { setPosition(await requestEphemeralMapPosition()); setLocationState('idle') }
    catch (error) { setLocationState(error instanceof Error && error.message === 'denied' ? 'denied' : 'unavailable') }
  }
  return <section className={`worker-job-map worker-assignment-map ${compact ? 'worker-job-map--compact' : ''}`} aria-label={t('Worksite map')}>
    <div className="worker-job-map__toolbar"><span>{mapLocation.precision === 'exact' ? t('Exact worksite') : t('Approximate location')}</span>{assignment.distanceLabel ? <span>{assignment.distanceLabel}</span> : null}<button type="button" className="btn btn-secondary" disabled={locationState === 'loading'} onClick={() => void useLocation()}>{locationState === 'loading' ? t('Finding you…') : t('Use my location')}</button>{mapLocation.navigationAllowed ? <button type="button" className="btn btn-secondary" onClick={() => openDirections({ name: mapLocation.displayLabel, latitude: mapLocation.latitude, longitude: mapLocation.longitude })}>{t('Open Directions')}</button> : null}</div>
    {locationState === 'denied' ? <p className="worker-guidance">{t('Location permission is off. You can still view the worksite and open directions.')}</p> : null}
    {locationState === 'unavailable' ? <p className="worker-guidance">{t('My location is unavailable. You can still view the worksite.')}</p> : null}
    <div ref={containerRef} className="worker-job-map__canvas" />
  </section>
}
